import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai/react';
import { useCurrentUserId } from '@framework/auth';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { UserIconMenu } from './UserIconMenu';
import { DarkTooltip } from '@framework/ui/atoms/Tooltip';
import { ViewerIcons } from '@view-model/Viewer/ui';
import { NotificationIcon } from './NotificationIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faFile } from '@fortawesome/free-regular-svg-icons';
import { HelpPageIcon } from './HelpPageIcon';
import { TimerContainer } from './Timer';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import type { Workspace } from '@workspace/domain/workspace';
import { faDiamond } from '@fortawesome/free-solid-svg-icons';
import { useIsAIAssistantEnabled } from '../../../AIAssistant/useIsAIAssistantEnabled';
import { aiAssistantDrawerOpenAtom } from '../../../AIAssistant/aiAssistantDrawerAtom';
import type { ViewModelPermissions } from '@view-model/application/context';
export type CurrentMenu = 'files' | 'comments' | 'notifications' | 'none';

type Props = {
  setClientViewBox: (viewBox: Rect) => void;
  setCurrentMenu: (menu: CurrentMenu) => void;
  currentMenu: CurrentMenu;
  showTimer: boolean;
  onOpenTimer(): void;
  onCloseTimer(): void;
  viewModel: ViewModelEntity;
  workspace: Workspace;
  viewModelPermissions: ViewModelPermissions;
};

export const HeaderMenuRight: React.FC<Props> = ({
  setClientViewBox,
  setCurrentMenu,
  currentMenu,
  showTimer,
  onOpenTimer,
  onCloseTimer,
  viewModel,
  workspace,
  viewModelPermissions,
}: Props) => {
  const { t } = useTranslation();
  const currentUserId = useCurrentUserId();
  const { enabled: aiAssistantEnabled } = useIsAIAssistantEnabled(workspace.groupId);
  const [isAIAssistantDrawerOpen, setAIAssistantDrawerOpen] = useAtom(aiAssistantDrawerOpenAtom);

  const onToggleFileList = useCallback(
    () => setCurrentMenu(currentMenu === 'files' ? 'none' : 'files'),
    [currentMenu, setCurrentMenu]
  );
  const onToggleCommentList = useCallback(
    () => setCurrentMenu(currentMenu === 'comments' ? 'none' : 'comments'),
    [currentMenu, setCurrentMenu]
  );
  const onToggleNotifications = useCallback(
    () => setCurrentMenu(currentMenu === 'notifications' ? 'none' : 'notifications'),
    [currentMenu, setCurrentMenu]
  );

  const toggleAIAssistantDrawer = () => {
    setAIAssistantDrawerOpen((prev) => !prev);
  };

  return (
    <>
      <div className="flex justify-end gap-2">
        <div className="pointer-events-auto">
          <DarkTooltip
            tooltip={
              currentMenu === 'files' ? t('viewModel.headerMenu.file.hide') : t('viewModel.headerMenu.file.show')
            }
            placement={'bottom'}
          >
            <button
              className="flex size-12 cursor-pointer items-center justify-center rounded-lg bg-white text-2xl text-gray-700 shadow-md hover:bg-gray-200 focus:outline-none"
              onClick={onToggleFileList}
              aria-label={t('viewModel.headerMenu.file.label')}
            >
              <FontAwesomeIcon icon={faFile} />
            </button>
          </DarkTooltip>
        </div>

        <div className="pointer-events-auto">
          <DarkTooltip
            tooltip={
              currentMenu === 'comments'
                ? t('viewModel.headerMenu.comment.hide')
                : t('viewModel.headerMenu.comment.show')
            }
            placement={'bottom'}
          >
            <button
              className="flex size-12 cursor-pointer items-center justify-center rounded-lg bg-white text-2xl text-gray-700 shadow-md hover:bg-gray-200 focus:outline-none"
              onClick={onToggleCommentList}
              aria-label={t('viewModel.headerMenu.comment.label')}
            >
              <FontAwesomeIcon icon={faComments} />
            </button>
          </DarkTooltip>
        </div>

        {currentUserId && <ViewerIcons className="pointer-events-auto" setClientViewBox={setClientViewBox} />}

        <NotificationIcon
          className="pointer-events-auto"
          showNotifications={currentMenu === 'notifications'}
          onToggle={onToggleNotifications}
          showButtonShadow={true}
        />

        <HelpPageIcon className="pointer-events-auto" />

        <div className="pointer-events-auto">{currentUserId && <UserIconMenu userId={currentUserId} />}</div>

        {aiAssistantEnabled && viewModelPermissions.isContentEditable && (
          <div className="pointer-events-auto">
            <DarkTooltip tooltip={t('viewModel.aiChat.title')} placement={'bottom'}>
              <button
                className={`flex size-12 cursor-pointer items-center justify-center rounded-lg text-2xl shadow-md focus:outline-none ${
                  isAIAssistantDrawerOpen
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-200'
                }`}
                onClick={toggleAIAssistantDrawer}
                aria-label={t('viewModel.headerMenu.aiAssistant')}
                aria-pressed={isAIAssistantDrawerOpen}
              >
                <FontAwesomeIcon icon={faDiamond} />
              </button>
            </DarkTooltip>
          </div>
        )}
      </div>
      <TimerContainer
        showTimer={showTimer}
        onOpen={onOpenTimer}
        onClose={onCloseTimer}
        viewModel={viewModel}
        workspace={workspace}
      />
    </>
  );
};
